import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import type { People } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Briefcase, Building2, Mail, Phone } from 'lucide-react';
import { useCallback, useRef } from 'react';

interface PersonCardProps {
    person: People;
}

export function PersonCard({ person }: PersonCardProps) {
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();

    // Track touch events to distinguish scrolls from taps
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const isScrollingRef = useRef(false);

    // Handle touch start - record initial position
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
            isScrollingRef.current = false;
        }
    }, []);

    // Handle touch move - detect if user is scrolling
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!touchStartRef.current) return;

        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        const threshold = 10; // pixels - adjust if needed

        // If movement exceeds threshold, user is scrolling
        if (deltaX > threshold || deltaY > threshold) {
            isScrollingRef.current = true;
        }
    }, []);

    // Handle navigation - only navigate if it was a tap, not a scroll
    const handleNavigation = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const isTouch = 'touches' in e || 'changedTouches' in e;

            if (isTouch || window.innerWidth < 768) {
                // If it was a scroll gesture, don't navigate
                if (isScrollingRef.current) {
                    touchStartRef.current = null;
                    isScrollingRef.current = false;
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                router.visit(`/person/${person.id}`, {
                    preserveState: false,
                    preserveScroll: false,
                });
            }

            // Reset refs after handling
            touchStartRef.current = null;
            isScrollingRef.current = false;
        },
        [person.id],
    );

    // Ensure we have a valid ID
    if (!person?.id) {
        console.error('Person ID is missing:', person);
        return null;
    }

    return (
        <Link
            href={`/person/${person.id}`}
            className="touch-target block"
            onClick={handleNavigation}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleNavigation}
        >
            <Card className="mb-2 cursor-pointer transition-shadow hover:shadow-sm">
                <CardContent className="p-3">
                    <div className="flex flex-col gap-2">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold">{fullName}</h3>
                                {person.position && (
                                    <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                                        <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span className="truncate">{person.position}</span>
                                    </div>
                                )}
                            </div>
                            {person.accounting && (
                                <Badge variant="outline" className="flex-shrink-0 px-1.5 py-0 text-xs">
                                    Accounting
                                </Badge>
                            )}
                        </div>

                        {/* Details */}
                        <div className="space-y-1">
                            {person.company && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{person.company.name}</span>
                                </div>
                            )}

                            {person.email && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{person.email}</span>
                                </div>
                            )}

                            {person.phone && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{person.phone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
